import React, { useEffect, useMemo, useState } from "react";
import Icons from "../icons";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* The {NAME} variables of an App Page Test: the AppPage QA Agent chose their
   first values; change them here. The scenario keeps the names; its Artifact's
   script and every new execution use the saved values. A variable the agent
   found no real value for (it wrote a placeholder) starts empty, with its
   `hint` ("an existing material"): the test runs once it has a value. */
/* What an empty variable needs, from the AI's hint ("an existing property
   ID" → "No existing property ID was found. Enter a valid property ID to run
   the test."). */
export function needsValue(hint) {
  const what = String(hint || "").trim().replace(/^(?:an?|the)\s+/i, "").replace(/^existing\s+/i, "") || "value";
  return `No existing ${what} was found. Enter a valid ${what} to run the test.`;
}

export default function AppPageVariables({ testKey, variables, userkey, onSaved }) {
  const names = useMemo(() => Object.keys(variables || {}), [variables]);
  const saved = useMemo(
    () => Object.fromEntries(names.map((n) => [n, String(variables[n]?.value ?? "")])),
    [names, variables]
  );
  const [values, setValues] = useState(saved);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => setValues(saved), [saved]);

  if (!names.length) return null;
  const changed = names.filter((n) => (values[n] ?? "").trim() !== saved[n]);

  const save = async () => {
    setBusy(true);
    setErr("");
    setNote("");
    try {
      const res = await fetch(`${API_BASE_URL}app-pages/tests/${encodeURIComponent(testKey)}/variables`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          values: Object.fromEntries(changed.map((n) => [n, values[n].trim()])),
          userkey: userkey || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      const saved = `${changed.length} value${changed.length === 1 ? "" : "s"} saved.`;
      // The screen says so in a toast when it has one; else it is said here.
      if (onSaved) onSaved(body.data, saved);
      else setNote(saved);
    } catch (e) {
      setErr(`Could not save the variables: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label={`Variables of ${testKey}`} className="rounded-lg border border-slate-200 bg-card">
      <header className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
        <h4 className="min-w-0 flex-1 text-xs font-semibold text-slate-700">
          Variables
        </h4>
        <button
          type="button"
          onClick={save}
          disabled={busy || changed.length === 0}
          className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-2.5 py-1 text-[12px] font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
        >
          {busy ? <Icons.Spinner className="h-3.5 w-3.5 animate-spin" /> : <Icons.Check className="h-3.5 w-3.5" />}
          Save
        </button>
      </header>
      <ul className="divide-y divide-slate-100">
        {names.map((n) => {
          const initial = String(variables[n]?.initial ?? "");
          const label = variables[n]?.label || "";
          const hint = variables[n]?.hint || "";
          const empty = !saved[n].trim();
          return (
            <li key={n} className="flex flex-wrap items-center gap-2 px-3 py-2">
              <label htmlFor={`var-${testKey}-${n}`} className="w-48 min-w-0 flex-none">
                <span className="block truncate font-mono text-[11px] font-semibold text-slate-700">{`{${n}}`}</span>
                {label && <span className="block truncate text-[11px] text-slate-400">{label}</span>}
              </label>
              <input
                id={`var-${testKey}-${n}`}
                value={values[n] ?? ""}
                placeholder={hint ? `e.g. ${hint}` : ""}
                aria-describedby={empty ? `var-${testKey}-${n}-needs` : undefined}
                onChange={(e) => setValues((v) => ({ ...v, [n]: e.target.value }))}
                className={[
                  "min-w-[12rem] flex-1 rounded-lg border bg-white px-2.5 py-1 text-sm text-slate-800 placeholder-slate-400 focus:border-blue-400 focus:outline-none",
                  empty ? "border-amber-300" : "border-slate-200",
                ].join(" ")}
              />
              {empty && (
                <span id={`var-${testKey}-${n}-needs`} className="w-full text-[11px] text-amber-800">
                  {needsValue(hint)}
                </span>
              )}
              {initial && (values[n] ?? "") !== initial && (
                <button
                  type="button"
                  onClick={() => setValues((v) => ({ ...v, [n]: initial }))}
                  title={`The AI's value: ${initial}`}
                  className="text-[11px] font-medium text-blue-600 hover:underline"
                >
                  Reset
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {(err || note) && (
        <p role={err ? "alert" : "status"} className={`px-3 py-2 text-xs ${err ? "text-red-600" : "text-emerald-700"}`}>
          {err || note}
        </p>
      )}
    </section>
  );
}
